/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import _ from 'lodash';
import dayjs from 'dayjs';
import BaseControl from 'scripts/base-control';
import DomEventsManager from 'scripts/dom-events-manager';
import dom from 'scripts/utils/dom';
import dtm from 'scripts/services/dtm';
import eventInfoModel from './event-hero-info.stache';
import videoTemplate from './event-hero-video.stache';
import Modernizr from 'modernizr';

export const EventHeroModule = BaseControl.extend({
    init () {
        this.$eventDescription = this.$element.find('.js-event-description');
        this.$eventVideo = this.$element.find('.js-event-video');
        this.$eventVideoWrapper = this.$element.find('.js-video-wrapper');
        this.screen = this.options.appContext.model.screen;

        this.addEventDescription();

        if (this.options.liveStream && this.options.liveStream.id) {
            DomEventsManager.subscribe(this);
        }

        this._super();

        this.$eventVideoWrapper.find('brightcove-video').viewModel()
            .on('fullscreenchange', () => {
                this.$eventVideoWrapper.toggleClass('is-fullscreen');
            })
            .on('play', this.onVideoPlay.bind(this));
    },

    onLayout () {
        const desktopLayout = this.screen.attr('screenSize') === 'large' ||
            this.screen.attr('screenSize') === 'extra-large';

        if (dom.isBrowserInFullscreenMode() && this.$eventVideo.html()) {
            return;
        }

        if (!desktopLayout && this.$eventVideo.html()) {
            return;
        }

        this.addEventVideo(desktopLayout);
    },

    addEventDescription () {
        let eventDateSummary = null;
        let tvBroadcastSummary = null;
        let isBroadcast = null;
        let hasTime = null;

        if (this.options.eventStart && this.options.eventEnd) {
            const champStartDate = dayjs(this.options.eventStart);
            const champEndDate = dayjs(this.options.eventEnd);

            eventDateSummary = createEventDate(champStartDate, champEndDate);
            tvBroadcastSummary = createBroadcastObject(champStartDate, champEndDate, this.options.tvBroadcastSummary);
            isBroadcast = isBroadcastPresent(tvBroadcastSummary);
            hasTime = isTimeSpecified(tvBroadcastSummary);
        }

        this.$eventDescription.html(eventInfoModel({
            eventName: this.options.eventName,
            courseName: this.options.courseName,
            eventLocation: this.options.eventLocation,
            courseName2: this.options.courseName2 || '',
            eventLocation2: this.options.eventLocation2 || '',
            eventDateSummary,
            tvBroadcastSummary,
            isBroadcast,
            hasTime
        }));

        function createEventDate (champStartDate, champEndDate) {
            const isMonthsEquals = champStartDate.month() === champEndDate.month();
            const isDatesEquals = isMonthsEquals && champStartDate.date() === champEndDate.date();
            const datesRange = isMonthsEquals
                ? `${champStartDate.format('MMMM').toUpperCase()} ${isDatesEquals ? champStartDate.date() : `${champStartDate.date()}-${champEndDate.date()}`}`
                : `${champStartDate.format('MMMM').toUpperCase()} ${champStartDate.date()} - ${champEndDate.format('MMMM').toUpperCase()} ${champEndDate.date()}`;

            return `${datesRange}, ${champStartDate.year()}`;
        }

        function createBroadcastObject (champStartDate, champEndDate, broadcasts) {
            const currentDate = dayjs();
            let tvBroadcastSummary = [];

            if (currentDate.isBefore(champEndDate)) {
                if (currentDate.isBefore(champStartDate, 'day')) {
                    tvBroadcastSummary = createPreChampionshipsBroadcast(currentDate, tvBroadcastSummary, champStartDate, champEndDate, broadcasts);
                } else {
                    tvBroadcastSummary = createDuringChampionshipsBroadcast(currentDate, champStartDate, champEndDate, broadcasts);
                }
            }

            return tvBroadcastSummary;
        }

        /**
         * Check time of day
         * @param { Moment } startTime
         * @param { Moment } endTime
         */
        function getFormattedTime (startTime, endTime) {
            const startTimeFormat = `h:mm${startTime.format('A') === endTime.format('A') ? '' : ' A'}`;
            const endTimeFormat = 'h:mm A';

            return `${startTime.format(startTimeFormat)}-${endTime.format(endTimeFormat)}`;
        }

        function createPreChampionshipsBroadcast (currentDate, tvBroadcastSummary, champStartDate, champEndDate, broadcasts) {
            let groupedBroadcasts = {};
            let prevProvider = '';

            for (let i = 0; i < broadcasts.length; i++) {
                let broadcast = broadcasts[i];
                let date = '';
                let time = '';

                if (broadcast.broadcastStart && broadcast.broadcastEnd) {
                    const broadcastStartDate = dayjs(broadcast.broadcastStart);
                    const broadcastEndDate = dayjs(broadcast.broadcastEnd);

                    const broadcastProvider = broadcast.channelProviderTitle;

                    if (!groupedBroadcasts[broadcastProvider]) {
                        if (Object.keys(groupedBroadcasts).length >= 2) {
                            break;
                        }
                        groupedBroadcasts[broadcastProvider] = [broadcast];
                    } else {
                        if (prevProvider === broadcastProvider) {
                            groupedBroadcasts[broadcastProvider].push(broadcast);
                        }
                    }

                    prevProvider = broadcastProvider;

                    if (currentDate < broadcastEndDate) {
                        date = `${broadcastStartDate.format('M/D')}-${broadcastEndDate.format('M/D')}`;
                        time = getFormattedTime(broadcastStartDate, broadcastEndDate);
                    } else {
                        return null;
                    }

                    _.merge(broadcast, {date, time});
                }
            }

            for (let channel in groupedBroadcasts) {
                let group = groupedBroadcasts[channel];

                let firstBroadcast = group[0];
                let lastBroadcast = group[group.length - 1];

                let firstBroadcastStart = dayjs(firstBroadcast.broadcastStart);
                let lastBroadcastStart = dayjs(lastBroadcast.broadcastStart);

                let date = `${firstBroadcastStart.format('M/D')}-${lastBroadcastStart.format('M/D')}`;

                if (firstBroadcastStart.isSame(lastBroadcastStart, 'day')) {
                    date = firstBroadcastStart.format('M/D');
                }

                let hideTime = false;
                for (let i = 1; i < group.length; i++) {
                    if (group[i].time !== group[i - 1].time) {
                        hideTime = true;
                    }
                }

                let time = hideTime ? '' : firstBroadcast.time;

                firstBroadcast.date = date;
                firstBroadcast.time = time;

                tvBroadcastSummary.push(group[0])
            }

            return tvBroadcastSummary;
        }

        function createDuringChampionshipsBroadcast (currentDate, champStartDate, champEndDate, broadcasts) {
            let tvBroadcastSummary = broadcasts.map((broadcast) => {
                let date = '';
                let time = '';

                if (broadcast.broadcastStart && broadcast.broadcastEnd) {
                    const broadcastStartDate = dayjs(broadcast.broadcastStart);
                    const broadcastEndDate = dayjs(broadcast.broadcastEnd);

                    if (currentDate < broadcastEndDate) {
                        date = `${broadcastStartDate.format('M/D')}-${broadcastEndDate.format('M/D')}`;
                        time = getFormattedTime(broadcastStartDate, broadcastEndDate);

                        if (broadcastStartDate.isSame(broadcastEndDate, 'day')) {
                            date = broadcastStartDate.format('M/D');
                        }
                    } else {
                        return null;
                    }
                }

                return _.merge(broadcast, {date, time});
            });

            return tvBroadcastSummary;
        }

        function isBroadcastPresent (broadcasts) {
            return broadcasts.some((broadcast) => broadcast != null);
        }

        function isTimeSpecified (broadcasts) {
            return broadcasts.some((broadcast) => broadcast != null && broadcast.time !== '');
        }
    },

    addEventVideo (desktopLayout) {
        const videoId = this.options.liveStream.id;

        if (!videoId) {
            this.$eventVideoWrapper.hide();
            return;
        }

        Modernizr.on('videoautoplay', (autoplay) => {
            this.$eventVideo.html(videoTemplate({
                autoplay: desktopLayout && autoplay,
                muted: desktopLayout,
                image: this.options.liveStream.image,
                videoId,
                playerId: this.options.playerId
            }));
        });
    },

    onWindowScroll () {
        this.scrollY = window.pageYOffset;
        this.moduleY = this.$element.offset().top;
        this.moduleHeight = this.$element.height();

        this.onScrolled();
    },

    onScrolled () {
        this.$eventVideoWrapper.toggleClass('is-scrolled', this.moduleHeight + this.moduleY <= this.scrollY)
    },

    '.js-video-close-btn click': function () {
        const state = videojs(this.$eventVideo.find('video').attr('id')).readyState();
        if (state === 0 || state === 1) {
            return;
        }

        videojs(this.$eventVideo.find('video').attr('id')).pause();
        this.$eventVideoWrapper.remove();
        dtm.trackChampionshipsEventHeroVideoClose();
    },

    '.js-play-mobile click': function (event) {
        this.$eventVideoWrapper.find('brightcove-video, live-brightcove-video').viewModel().toggleFullScreen();
        videojs(this.$eventVideoWrapper.find('video').attr('id')).play();
        dtm.trackChampionshipsEventHeroVideoStart();
    },

    onVideoPlay: function () {
        dtm.trackChampionshipsEventHeroVideoStart();
    }
});
